import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { toastEmailCodeResult } from '../utils/mailFeedback'
import { homePathForRole } from '../utils/routes'
import { AuthSignupLayout, AuthProgressSteps } from '../components/Auth/AuthShell'
import LanguageMultiSelect from '../components/Auth/LanguageMultiSelect'
import {
  SIGNUP_ROLE_CARDS,
  COUNTRY_OPTIONS,
  SECTOR_TAGS,
  GEO_TAGS,
  resolveSignupRole,
} from '../constants/signupRoles'
import { RsIcon } from '../components/icons'

function toggleInList(list, item) {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item]
}

export default function SignUp() {
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get('invite')
  const navigate = useNavigate()

  const [step, setStep] = useState(1)
  const [inviteInfo, setInviteInfo] = useState(null)
  const [loading, setLoading] = useState(false)
  const [verifyCode, setVerifyCode] = useState('')
  const [countdown, setCountdown] = useState(0)
  const [emailError, setEmailError] = useState('')

  const [selectedRoleId, setSelectedRoleId] = useState('GP')
  const [languages, setLanguages] = useState(['en'])
  const [sectors, setSectors] = useState(['Consumer & Retail'])
  const [geoFocus, setGeoFocus] = useState(['🇨🇳 Tier 1 Cities (Beijing/Shanghai)', '🇨🇳 Tier 2 Cities'])

  const [formData, setFormData] = useState({
    workEmail: '',
    realName: '',
    companyFullName: '',
    companyShortName: '',
    position: '',
    phone: '',
    website: '',
    natureOfBusiness: '',
    country: 'US',
    password: '',
    confirmPassword: '',
  })

  const roleMeta = resolveSignupRole(selectedRoleId)
  const backendRole = roleMeta.backendRole

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  useEffect(() => {
    if (inviteToken) {
      axios
        .get(`/api/companies/invites/preview?token=${inviteToken}`)
        .then((r) => {
          setInviteInfo(r.data)
          setFormData((prev) => ({
            ...prev,
            workEmail: r.data.invitedEmail || prev.workEmail,
            companyFullName: r.data.companyName || prev.companyFullName,
            companyShortName: r.data.companyShortName || prev.companyShortName,
          }))
          if (r.data.suggestedRole) {
            const match = SIGNUP_ROLE_CARDS.find((c) => c.backendRole === r.data.suggestedRole)
            if (match) setSelectedRoleId(match.id)
          }
          toast.success(`Loaded invitation for ${r.data.companyName}`)
        })
        .catch(() => toast.error('Invite link is invalid or expired'))
    }
  }, [inviteToken])

  const sendVerifyCode = async () => {
    if (!formData.workEmail) {
      toast.error('Please enter your work email')
      return
    }
    try {
      const res = await axios.post('/api/auth/send-email-code', {
        email: formData.workEmail,
        type: 'registration',
      })
      toastEmailCodeResult(res, toast)
      setCountdown(60)
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) clearInterval(timer)
          return prev - 1
        })
      }, 1000)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send code')
    }
  }

  const validateStep1 = () => {
    setEmailError('')
    if (!formData.realName?.trim()) {
      toast.error('Please enter your full name')
      return false
    }
    if (!formData.companyFullName?.trim()) {
      toast.error('Please enter company name')
      return false
    }
    if (!formData.natureOfBusiness?.trim()) {
      toast.error('Please enter nature of business')
      return false
    }
    if (!formData.workEmail?.includes('@')) {
      setEmailError('Please enter a valid email address.')
      return false
    }
    if (!formData.position?.trim()) {
      updateField('position', formData.natureOfBusiness)
    }
    if (!formData.companyShortName?.trim()) {
      const short = formData.companyFullName.split(/\s+/)[0]?.slice(0, 20) || 'Co'
      updateField('companyShortName', short)
    }
    return true
  }

  const handleStep1 = (e) => {
    e.preventDefault()
    if (!validateStep1()) return
    if (!languages.length) {
      toast.error('Please select at least one preferred language')
      return
    }
    setStep(2)
  }

  const handleStep2Verify = async (e) => {
    e.preventDefault()
    if (!verifyCode) {
      toast.error('Please enter verification code')
      return
    }
    setLoading(true)
    try {
      await axios.post('/api/auth/verify-email-code', {
        email: formData.workEmail,
        code: verifyCode,
      })
      toast.success('Email verified!')
      setStep(3)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid code')
    } finally {
      setLoading(false)
    }
  }

  const handleComplete = async (e) => {
    e.preventDefault()
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }
    setLoading(true)
    try {
      const profileMeta = {
        signupRoleLabel: roleMeta.label,
        serviceSubtype: roleMeta.serviceSubtype || null,
        country: formData.country,
        website: formData.website,
        languages,
        defaultLanguage: languages[0] || 'en',
        sectors: backendRole === 'LP' ? sectors : undefined,
        geoFocus: backendRole === 'LP' ? geoFocus : undefined,
      }
      const res = await axios.post('/api/auth/complete-registration', {
        ...formData,
        role: backendRole,
        emailVerifyCode: verifyCode,
        inviteToken: inviteToken || undefined,
        profileMeta,
      })
      if (res.data.pendingReview) {
        toast.success('Submitted for review — awaiting admin approval')
        navigate('/login')
        return
      }
      if (res.data.token) {
        localStorage.setItem('token', res.data.token)
        axios.defaults.headers.common.Authorization = `Bearer ${res.data.token}`
        toast.success('Registration successful')
        navigate(`/account-created?role=${encodeURIComponent(res.data.user?.role || backendRole)}`)
        return
      }
      toast.success('Registration submitted!')
      navigate('/login')
    } catch (err) {
      const msg = err.response?.data?.message || 'Registration failed'
      if (err.response?.data?.code === 'SEAT_LIMIT') {
        toast.error(`${msg} — contact your company admin for an invite link`)
      } else {
        toast.error(msg)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthSignupLayout
      stepLabel={`Create your account — Step ${step} of 3`}
      headerExtra={
        inviteInfo ? null : (
          <div className="auth-footer-link" style={{ marginTop: 0, textAlign: 'right' }}>
            Already have an account?
            <br />
            <Link to="/login">Login →</Link>
          </div>
        )
      }
    >
      {inviteInfo && (
        <div className="auth-pref-box" style={{ marginBottom: 20 }}>
          <div className="auth-pref-box-title">Company invitation</div>
          <div className="auth-pref-box-desc">
            Joining <strong>{inviteInfo.companyName}</strong> ({inviteInfo.invitedEmail})
          </div>
        </div>
      )}

      <AuthProgressSteps steps={['Account', 'Verify Email', 'Password']} current={step} />

      {step === 1 && (
        <form onSubmit={handleStep1}>
          <div className="auth-form-group">
            <label className="auth-label">
              I&apos;m a/an…<span className="auth-sublabel">(required)</span>
            </label>
            <div className="auth-role-grid">
              {SIGNUP_ROLE_CARDS.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className={`auth-role-card${selectedRoleId === r.id ? ' selected' : ''}`}
                  onClick={() => setSelectedRoleId(r.id)}
                >
                  <div className="auth-role-name">{r.label}</div>
                  <div className="auth-role-desc">{r.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="auth-form-group">
            <label className="auth-label">
              Full Name<span className="auth-sublabel">(required)</span>
            </label>
            <input
              type="text"
              className="auth-input"
              placeholder="Your full legal name"
              value={formData.realName}
              onChange={(e) => updateField('realName', e.target.value)}
              required
            />
          </div>

          <div className="auth-form-group">
            <label className="auth-label">
              Company / Organization<span className="auth-sublabel">(required)</span>
            </label>
            <input
              type="text"
              className="auth-input"
              placeholder="Company name"
              value={formData.companyFullName}
              onChange={(e) => updateField('companyFullName', e.target.value)}
              required
            />
          </div>

          <div className="auth-form-row-2">
            <div className="auth-form-group">
              <label className="auth-label">Mobile Number</label>
              <input
                type="tel"
                className="auth-input"
                placeholder="+86 …"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
              />
            </div>
            <div className="auth-form-group">
              <label className="auth-label">Company Website</label>
              <input
                type="url"
                className="auth-input"
                placeholder="https://…"
                value={formData.website}
                onChange={(e) => updateField('website', e.target.value)}
              />
            </div>
          </div>

          <div className="auth-form-group">
            <label className="auth-label">
              Nature of Business<span className="auth-sublabel">(required)</span>
            </label>
            <input
              type="text"
              className="auth-input"
              placeholder="e.g. Private Equity, Venture Capital, Family Office…"
              value={formData.natureOfBusiness}
              onChange={(e) => updateField('natureOfBusiness', e.target.value)}
              required
            />
          </div>

          <div className="auth-form-group">
            <label className="auth-label">
              Work Email<span className="auth-sublabel">(required)</span>
            </label>
            <input
              type="email"
              className={`auth-input${emailError ? ' error' : ''}`}
              placeholder="you@company.com"
              value={formData.workEmail}
              onChange={(e) => {
                updateField('workEmail', e.target.value)
                setEmailError('')
              }}
              required
            />
          </div>
          {emailError && <div className="auth-error">{emailError}</div>}

          <div className="auth-form-group">
            <label className="auth-label">
              Country / Region<span className="auth-sublabel">(required)</span>
            </label>
            <select
              className="auth-input"
              value={formData.country}
              onChange={(e) => updateField('country', e.target.value)}
            >
              {COUNTRY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="auth-form-group">
            <label className="auth-label">Preferred Language for Communication</label>
            <LanguageMultiSelect value={languages} onChange={setLanguages} max={3} />
          </div>

          <div className="auth-btn-row" style={{ justifyContent: 'flex-end' }}>
            <button type="submit" className="auth-btn auth-btn-primary" style={{ width: 'auto', padding: '12px 32px' }}>
              Continue
            </button>
          </div>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handleStep2Verify}>
          {backendRole === 'LP' && (
            <>
              <div className="auth-pref-box">
                <div className="auth-pref-box-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <RsIcon name="deal" size="sm" /> Investment Preferences
                </div>
                <div className="auth-pref-box-desc">
                  Select sectors and regions you&apos;re interested in. This helps us match you with
                  relevant deals.
                </div>
              </div>
              <div className="auth-form-group">
                <label className="auth-label">
                  Preferred Sectors<span className="auth-sublabel">(select all that apply)</span>
                </label>
                <div className="auth-tags">
                  {SECTOR_TAGS.map((tag) => (
                    <span
                      key={tag}
                      role="button"
                      tabIndex={0}
                      className={`auth-tag${sectors.includes(tag) ? ' selected' : ''}`}
                      onClick={() => setSectors(toggleInList(sectors, tag))}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="auth-form-group">
                <label className="auth-label">
                  Geographic Focus<span className="auth-sublabel">(select all that apply)</span>
                </label>
                <div className="auth-tags">
                  {GEO_TAGS.map((tag) => (
                    <span
                      key={tag}
                      role="button"
                      tabIndex={0}
                      className={`auth-tag${geoFocus.includes(tag) ? ' selected' : ''}`}
                      onClick={() => setGeoFocus(toggleInList(geoFocus, tag))}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="auth-verify-icon"><RsIcon name="email" size="xl" /></div>
          <h2 className="auth-verify-title">Verify Your Email</h2>
          <p className="auth-verify-sub">
            We sent a 6-digit code to <strong>{formData.workEmail}</strong>
          </p>

          <div className="auth-form-group">
            <label className="auth-label">Verification Code</label>
            <input
              type="text"
              className="auth-input auth-input--code"
              placeholder="000000"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value)}
              maxLength={6}
            />
          </div>

          <div className="auth-resend-row">
            <span className="auth-sublabel" style={{ margin: 0 }}>
              Didn&apos;t receive the code?
            </span>
            <button
              type="button"
              className="auth-link"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={sendVerifyCode}
              disabled={countdown > 0}
            >
              {countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
            </button>
          </div>

          <div className="auth-btn-row">
            <button type="button" className="auth-btn auth-btn-ghost" onClick={() => setStep(1)}>
              ← Back
            </button>
            <button
              type="button"
              className="auth-btn auth-btn-outline"
              onClick={sendVerifyCode}
              disabled={countdown > 0}
            >
              {countdown > 0 ? `${countdown}s` : 'Send Code'}
            </button>
            <button
              type="submit"
              className="auth-btn auth-btn-primary"
              style={{ width: 'auto', flex: 1 }}
              disabled={loading}
            >
              {loading ? 'Verifying…' : 'Continue →'}
            </button>
          </div>
        </form>
      )}

      {step === 3 && (
        <form onSubmit={handleComplete}>
          <div className="auth-subtitle" style={{ marginBottom: 24 }}>
            Set your password
          </div>

          <div className="auth-form-group">
            <label className="auth-label">Password</label>
            <input
              type="password"
              className="auth-input"
              placeholder="Min 8 characters"
              value={formData.password}
              onChange={(e) => updateField('password', e.target.value)}
              minLength={8}
              required
            />
          </div>
          <div className="auth-form-group">
            <label className="auth-label">Confirm Password</label>
            <input
              type="password"
              className="auth-input"
              placeholder="Repeat password"
              value={formData.confirmPassword}
              onChange={(e) => updateField('confirmPassword', e.target.value)}
              required
            />
          </div>

          <div className="auth-btn-row" style={{ justifyContent: 'flex-end' }}>
            <button type="button" className="auth-btn auth-btn-ghost" onClick={() => setStep(2)}>
              ← Back
            </button>
            <button
              type="submit"
              className="auth-btn auth-btn-primary"
              style={{ width: 'auto', padding: '14px 40px' }}
              disabled={loading}
            >
              {loading ? 'Submitting…' : 'Complete Registration'}
            </button>
          </div>
        </form>
      )}
    </AuthSignupLayout>
  )
}
